import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavBar, Card, Space, Button, List } from 'antd-mobile';
import { LeftOutline, GlobalOutline, CheckOutline } from 'antd-mobile-icons';
import { useI18n } from '@/hooks/useI18n';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function LanguageSettings() {
  const navigate = useNavigate();
  const { t, changeLanguage, isLanguage } = useI18n();

  const languages = [
    { code: 'zh', name: '中文', englishName: 'Chinese' },
    { code: 'en', name: 'English', englishName: 'English' }
  ];

  const handleLanguageSelect = (langCode: string) => {
    changeLanguage(langCode);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center">
      <div className="w-full min-h-screen relative overflow-hidden" style={{ maxWidth: '480px' }}>
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <NavBar 
          onBack={() => navigate('/profile')}
          className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50"
        >
          <div className="flex items-center text-gray-800">
            <GlobalOutline className="mr-2" />
            语言设置
          </div>
        </NavBar>
      </motion.div>

      <div className="p-4 relative z-10">
        {/* 当前语言显示 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full mr-3" />
                <GlobalOutline className="mr-2" />
                当前语言
              </h3>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100"
              >
                <LanguageSwitcher showIcon={false} size="large" />
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 语言选择列表 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-3" />
                选择语言
              </h3>
              <div className="space-y-3">
                {languages.map((lang, index) => (
                  <motion.div
                    key={lang.code}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                    whileHover={{ x: 4, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                      isLanguage(lang.code)
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-md'
                        : 'bg-gradient-to-r from-gray-50/50 to-gray-100/50 border border-gray-200 hover:border-blue-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${
                          isLanguage(lang.code)
                            ? 'bg-gradient-to-br from-blue-500 to-indigo-500'
                            : 'bg-gradient-to-br from-gray-400 to-gray-500'
                        }`}>
                          <GlobalOutline className="text-white" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-800">
                            {lang.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {lang.englishName}
                          </div>
                        </div>
                      </div>
                      {isLanguage(lang.code) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center"
                        >
                          <CheckOutline className="text-white text-sm" />
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 语言说明 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-red-500 rounded-full mr-3" />
                语言说明
              </h3>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-100"
              >
                <p className="text-gray-600 text-sm leading-relaxed">
                  更改语言设置将立即生效，界面将切换到所选语言
                </p>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 返回按钮 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-6"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              color="primary"
              block
              onClick={() => navigate('/profile')}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0 rounded-xl h-12 font-medium shadow-lg"
            >
              返回
            </Button>
          </motion.div>
        </motion.div>

        {/* 底部间距 */}
        <div className="h-8" />
      </div>
      </div>
    </div>
  );
}